import { Component, OnInit } from '@angular/core';
import { SessionService } from 'src/app/auth/services/session.service';
import { TableConfig } from '../../shared/components/app-table/app-table-models';
import { WarningsDTO } from '../../shared/models/models';
import { EarlyWarningService } from './early-warning.service';

export enum UserType {
  intern,
  extern,
}
@Component({
    selector: 'lib-larm',
    templateUrl: './larm.component.html',
    styleUrls: ['./larm.component.scss'],
    standalone: false
})
export class LarmComponent implements OnInit {
  protected activeIndex = 13;
  protected dates: string[] = [];
  protected warningsByLab!: any[];
  protected warningsByKriteria!: any[];
  protected warningsByProvId!: any[];
  protected showBy = 'labCode';

  protected tableConfig: TableConfig = {
    accordionContent: 'larm-details',
  };

  constructor(
    private earlyWarningService: EarlyWarningService,
    protected sessionService: SessionService,
  ) {}
  ngOnInit(): void {
    this.showBy = this.sessionService.isInternUser ? 'labCode' : 'kriteriaName';
    this.tableConfig.dataKey = this.sessionService.isInternUser ? 'accordionHeader' : 'itemName';

    this.initTableColums();
    for (let i = 0; i < 14; i++) {
      const date = new Date();
      date.setDate(new Date().getDate() - i);
      this.dates.unshift(date.toISOString().split('T')[0]);
    }
    this.getWarnings(this.dates[this.activeIndex]);
  }

  private initTableColums() {
    this.tableConfig.columns = [
      {
        field: 'total_warning',
        header: 'Totalt larm vald dag',
        sortByField: 'total_warning',
        mapView: (row) => row.totalWarnings ?? row.warnings?.length ?? row.data.warnings.length,
        // useAlternative: () => true,
        type: 'badge',
        classes: ['warning', 'ml-5'],
        headerClasses: ['w-25'],
      },
      {
        field: 'new_warning',
        header: 'Varav nya larm vald dag',
        sortByField: 'new_warning',
        type: 'badge',
        mapView: (row) => row.newWarnings ?? row.newWarnings ?? row.data.newWarnings,
        // useAlternative: () => true,
        classes: ['danger', 'mr-15'],
        headerClasses: ['w-25'],
      },
    ];
    if (this.sessionService.isInternUser) {
      this.tableConfig.columns.unshift({
        field: 'accordionHeader',
        header: this.showBy === 'labCode' ? 'Laboratorium' : 'Larmkriterie',
        sortByField: 'accordionHeader',
      });
      this.tableConfig.columns.unshift({
        field: 'toggler',
        header: '',
        isRowToggler: true,
      });
    } else if (this.showBy === 'kriteriaName') {
      this.tableConfig.columns.unshift({
        field: 'itemName',
        header: 'Larmkriterie',
        sortByField: 'itemName',
      });
      this.tableConfig.columns.unshift({
        field: 'toggler',
        header: '',
        isRowToggler: true,
      });
    } else {
      this.tableConfig.columns.unshift({
        field: 'provId',
        header: 'Prov ID',
        sortByField: 'provId',
      });
    }
  }

  /**
   * Function will get all warnings for a specific date. Warnings will be mapped according to lab (warningsByLab), according to criteria (warningsByKriteria) or according to provId (warningsByProvId)
   */
  private getWarnings(date: string) {
    this.earlyWarningService.getWarnings(date).subscribe((data: WarningsDTO) => {
      const warnings: any = [];

      for (const [labCode, allKriteria] of Object.entries(data.values as WarningsDTO)) {
        const kriteriaObjects: any[] = [];
        let totalWarnings = 0;
        for (const [kriteriaName, kriteraObject] of Object.entries(allKriteria.values)) {
          totalWarnings += (kriteraObject as any).warnings.length;

          (kriteraObject as any).warnings.sort((x: any) => (x.newWarning ? -1 : 1));
          kriteriaObjects.push({ ...(kriteraObject as any), itemName: kriteriaName });
        }
        warnings.push({
          accordionHeader: labCode,
          accordionItems: kriteriaObjects,
          newWarnings: (data.values as any)[labCode].newWarnings,
          totalWarnings: totalWarnings,
        });
      }

      this.warningsByLab = warnings;

      if (this.showBy === 'labCode' || (!this.sessionService.isInternUser && this.showBy === 'kriteriaName')) {
        this.tableConfig.rows = this.sessionService.isInternUser
          ? this.warningsByLab
          : this.warningsByLab[0]?.accordionItems;
      }
    });

    this.earlyWarningService.getWarningsByKriteria(date).subscribe((data: WarningsDTO) => {
      const warnings: any = [];

      for (const [kriteriaName, allLabs] of Object.entries(data.values as WarningsDTO)) {
        const labObjects: any[] = [];
        let totalWarnings = 0;
        for (const [labCode, labObject] of Object.entries(allLabs.values)) {
          totalWarnings += (labObject as any).warnings.length;

          (labObject as any).warnings.sort((x: any) => (x.newWarning ? -1 : 1));
          labObjects.push({ ...(labObject as any), itemName: labCode });
        }
        warnings.push({
          accordionHeader: kriteriaName,
          accordionItems: labObjects,
          newWarnings: (data.values as any)[kriteriaName].newWarnings,
          totalWarnings: totalWarnings,
        });
      }

      this.warningsByKriteria = warnings;
      if (this.showBy === 'kriteriaName' && this.sessionService.isInternUser) {
        this.tableConfig.rows = this.warningsByKriteria;
      }
    });

    this.earlyWarningService.getWarningsByProvId(date).subscribe((data: any) => {
      const prov: any = [];
      for (const lab in data.values) {
        for (const provValue in data.values[lab].values)
          prov.push({ provId: provValue, data: (data.values[lab].values as any)[provValue] });
      }

      this.warningsByProvId = prov;
      if (this.showBy === 'provId') {
        this.tableConfig.rows = this.warningsByProvId;
      }
    });
  }

  protected toggleShowBy(showBy: string) {
    this.showBy = showBy;
    this.initTableColums();
    if (this.sessionService.isInternUser) {
      this.tableConfig.dataKey = 'accordionHeader';
      this.tableConfig.rows = showBy === 'labCode' ? this.warningsByLab : this.warningsByKriteria;
    } else {
      if (showBy === 'kriteriaName') {
        this.tableConfig.rows = this.warningsByLab[0]?.accordionItems;
        this.tableConfig.dataKey = 'itemName';
      } else {
        this.tableConfig.rows = this.warningsByProvId;
      }
    }
  }

  protected previousDate() {
    if (this.activeIndex > 0) {
      this.activeIndex--;
      this.getWarnings(this.dates[this.activeIndex]);
    }
  }

  protected nextDate() {
    if (this.activeIndex < 13) {
      this.activeIndex++;
      this.getWarnings(this.dates[this.activeIndex]);
    }
  }

  protected getDate(date: string) {
    this.activeIndex = this.dates.indexOf(date);
    this.getWarnings(date);
  }
}
